import { inject, Injectable } from '@angular/core';
import { ConsultationTreatmentTypes } from '@enums/consultation-treatment-types';
import { MedicalHistoryQuestionIds } from '@enums/medical-history-question-ids';
import { QuestionnaireAnswers } from '@enums/questionnaire-answers';
import { UpsellSlugs } from '@enums/upsell-slugs';
import { environment } from '@environments/environment';
import { ConsultationQuestionAnswer } from '@models/consultation-request/consultation-question-answer';
import { ConsultationRequestRequest } from '@models/consultation-request/consultation-request-request';
import { OrderUpsell } from '@models/order-upsell';
import { ConsultationRequestService } from '@services/consultation-request.service';
import { ConsultationStorageService } from '@services/consultation-storage.service';
import { DynamicFormsService } from '@services/dynamic-forms.service';
import { ErrorLoggingService } from '@services/error-logging.service';

@Injectable({
  providedIn: 'root',
})
export class TreatmentConsultationQuestionnaireService {
  private consultationStorageService = inject(ConsultationStorageService);
  private dynamicFormsService = inject(DynamicFormsService);
  private errorLoggingService = inject(ErrorLoggingService);
  private consultationRequestService = inject(ConsultationRequestService);

  /**
   * Returns a promise with the ConsultationRequestRequest model that represents the backend payload.
   */
  public async getConsultationRequestPayload(): Promise<ConsultationRequestRequest> {
    let consultationRequest = new ConsultationRequestRequest();
    consultationRequest = this.setMedicalHistoryToConsultationPayload(
      consultationRequest,
      this.consultationStorageService.medicalHistoryQuestionnaireAnswers,
    );
    consultationRequest.pharmacy_id = this.getPharmacy();
    consultationRequest.first_name = this.consultationStorageService.patient.name;
    consultationRequest.last_name = this.consultationStorageService.patient.last_name;
    consultationRequest.email = this.consultationStorageService.patient.email;
    if (this.consultationStorageService.isConsultationDisqualified) {
      consultationRequest.consultation_status = this.consultationStorageService.consultationStatus;
    }

    consultationRequest.additional_info = await this.getAdditionalInfo();

    if (this.consultationStorageService.patient.birthday) {
      consultationRequest.date_of_birth = this.getDateOfBirth();
    } else {
      this.errorLoggingService.logError({
        message: 'Date of birth is not set in the patient information.',
        consultationId: this.consultationStorageService.consultationId,
      });
    }

    return consultationRequest;
  }

  /**
   * Builds the upsell object.
   */
  public getUpsellsWithPrice(): OrderUpsell[] {
    let upsells = [];

    this.getApplicableUpsells().forEach((upsellName: string) =>
      upsells.push({
        id: environment.consultationRequestUpsells[upsellName].id,
        name: upsellName,
        upsell_price: environment.consultationRequestUpsells[upsellName].price * 100,
        skipOrderAddon: environment.consultationRequestUpsells[upsellName].skipOrderAddon ?? false,
        discount: environment.consultationRequestUpsells[upsellName]?.discount,
      }),
    );

    return upsells;
  }

  /**
   * Checks if the upsells contain any of the partner treatment upsells.
   */
  public upsellsContainsPartnerTreatment(): boolean {
    return [UpsellSlugs.PartnerTreatment, UpsellSlugs.MaleBVPartnerTreatment].some((upsell) =>
      this.consultationStorageService.upsells.includes(upsell),
    );
  }

  /**
   * Gets the applicable upsells, excluding treatments that are already on the order.
   */
  private getApplicableUpsells(): string[] {
    const orderedTreatment = this.consultationRequestService.getOrderedTreatmentPreferences();

    return this.consultationStorageService.upsells.filter(
      (upsellName: string) =>
        !environment.consultationRequestUpsells[upsellName].skipOrderAddon || upsellName !== orderedTreatment,
    );
  }

  /**
   * Sets the medical history information on the provided consultation request payload.
   *
   * @param {ConsultationRequestRequest} payload the consultation request payload to update
   *
   * @returns {ConsultationRequestRequest} the payload with the medical history set
   * @param answers
   */
  private setMedicalHistoryToConsultationPayload(
    payload: ConsultationRequestRequest,
    answers: object,
  ): ConsultationRequestRequest {
    payload.allergies = answers[MedicalHistoryQuestionIds.AllergiesDetails] ?? QuestionnaireAnswers.No;
    payload.health_info = answers[MedicalHistoryQuestionIds.OtherConditionsDetails] ?? QuestionnaireAnswers.No;
    payload.prescriptions = answers[MedicalHistoryQuestionIds.MedicationDetails] ?? QuestionnaireAnswers.No;

    return payload;
  }

  /**
   * Parse the treatment preferences into the questionnaire
   *
   * @param {ConsultationQuestionAnswer[]} questionnaire the questionnaire to push the treatment preferences into
   */
  private parseTreatmentPreferencesIntoQuestionnaire(questionnaire: ConsultationQuestionAnswer[]): void {
    questionnaire.unshift({
      question: 'Preferred Medication/Treatment',
      answer: this.consultationStorageService.treatmentPreferences,
    });
  }

  /**
   * Get the additional info for the consultation
   *
   * @returns {Promise<string>} promise that resolves to a JSON string with the additional consultation information
   */
  private async getAdditionalInfo(): Promise<string> {
    const patient = this.consultationStorageService.patient;
    const additional_info = {
      questionnaire: [],
      height: {
        feet: patient.height_feet,
        inches: patient.height_inches,
      },
      weight: patient.weight,
    };

    additional_info.questionnaire = await this.getParsedQuestionsAndAnswers();

    return JSON.stringify(additional_info);
  }

  /**
   * Retrieves and parses the questions and answers from the values stored in localStorage.
   *
   * @returns {Promise<ConsultationQuestionAnswer[]>} promise that resolves to an array of parsed question-answer pairs
   */
  private async getParsedQuestionsAndAnswers(): Promise<ConsultationQuestionAnswer[]> {
    const questionsAndAnswers = await this.dynamicFormsService.parseQuestionsAndAnswersFromStorage(
      this.consultationStorageService.treatmentType,
      {
        treatmentName: this.consultationStorageService.getTreatmentPreferenceName(),
      },
    );

    if (this.consultationStorageService.treatmentType === ConsultationTreatmentTypes.Glp1) {
      this.appendBmiScore(questionsAndAnswers);
    }

    questionsAndAnswers.unshift(this.consultationRequestService.getAdditionalRequests());
    this.parseTreatmentPreferencesIntoQuestionnaire(questionsAndAnswers);

    return this.moveBreastfeedingQuestionsToBottom(questionsAndAnswers);
  }

  /**
   * Moves the breastfeeding questions to the bottom of the questionnaire.
   *
   * @param {ConsultationQuestionAnswer []} questionsAndAnswers the questions and answers to be reordered
   *
   * @returns {ConsultationQuestionAnswer[]} the questions and answers with the breastfeeding questions at the bottom
   */
  private moveBreastfeedingQuestionsToBottom(
    questionsAndAnswers: ConsultationQuestionAnswer[],
  ): ConsultationQuestionAnswer[] {
    return [
      ...questionsAndAnswers.filter(
        (question) => !environment.consultation.breastfeedingQuestionIds.includes(question.id),
      ),
      ...questionsAndAnswers.filter((question) =>
        environment.consultation.breastfeedingQuestionIds.includes(question.id),
      ),
    ].map((question) => ({
      question: question.question,
      answer: question.answer,
    }));
  }

  /**
   * Appends the BMI score to the questionnaire.
   *
   * @param {ConsultationQuestionAnswer[]} questionnaire the questionnaire to push the BMI score into
   */
  private appendBmiScore(questionnaire: ConsultationQuestionAnswer[]): void {
    questionnaire.push({
      question: 'BMI Score',
      answer: this.consultationStorageService.bmiScore.toString(),
    });
  }

  /**
   * Get the pharmacy id
   *
   * If the shipping address is set, it will return the delivery pharmacy id
   */
  private getPharmacy(): number {
    if (this.consultationRequestService.shouldUseDeliveryPharmacy()) {
      return environment.consultation.deliveryPharmacyId;
    }

    return this.consultationStorageService.pharmacy.id;
  }

  /**
   * Get the date of birth in the format of YYYY-MM-DD
   */
  private getDateOfBirth(): string {
    const { year, month, day } = this.consultationStorageService.patient.birthday;

    return `${year.toString()}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
  }
}
